import { useCallback } from 'react';

import { PaymentMethodTypes } from '@givebacks/api/core';
import {
  Card,
  Center,
  ConditionalWrapper,
  Flex,
  Group,
  ImageMantine,
  Stack,
  Swipeable,
  Text,
  ThemeIcon,
  Transition,
  cx,
} from 'ui';
import { isNotEmpty, useForm } from 'ui/Form';
import { IconDelete } from 'ui/Icons';

import { ICredential } from '../../../api';
import { useBreakPoints, useFormListValidator } from '../../../hooks';
import { StripeCVV } from '../../molecules/forms/stripe/StripeCVV';
import classes from './ExistingCreditCard.module.css';

export interface ExistingCreditCardProps {
  credential: ICredential;
  onClick?: () => void;
  isActive?: boolean;
  isError?: boolean;
  variant?: 'default' | 'secondary';
}

export type ExistingCredentialsForm = {
  credentialId?: ICredential['uuid'];
  card_type: '' | PaymentMethodTypes;
  isCvvValid?: boolean;
};

export type ExistingCredentialForm = {
  isCvvValid?: boolean;
  isValidEntry: boolean;
};
export const ExistingCreditCard = ({
  credential,
  isActive = false,
  isError = false,
  variant = 'default',
  onClick,
}: ExistingCreditCardProps) => {
  return (
    <Card
      radius="sm"
      className={cx(classes.ExistingCreditCard__cardBase, {
        [classes['ExistingCreditCard--activeCard']]: isActive === true,
        [classes['ExistingCreditCard--erroredCard']]: isError === true,
        [classes['ExistingCreditCard--secondaryCard']]: variant === 'secondary',
      })}
      bd="1px solid gray.1"
      onClick={onClick}
    >
      <Group justify="space-between" mih="40px" wrap="nowrap">
        <Group wrap="nowrap">
          <ImageMantine fit="contain" w={62} h="auto" src={credential.image} />
          <Flex>
            <Text fz={14} mr="xs">{`•••• ••••`}</Text>
            <Text fz={14}>{`•••• ${credential.last_four}`}</Text>
          </Flex>
        </Group>
        <Text fz={14} c={'gray.4'}>
          {`${credential.month}/${credential.year.slice(2, 4)}`}
        </Text>
      </Group>
    </Card>
  );
};
type SwipeAbleCardProps = {
  onAction: () => void;
} & ExistingCreditCardProps;

const SwipeableCard = ({ onAction, onClick, credential, ...props }: SwipeAbleCardProps) => {
  const { isSmallScreen } = useBreakPoints();
  return (
    <ConditionalWrapper
      condition={!!isSmallScreen}
      wrapper={(children) => (
        <Swipeable
          rightAction={(props) => (
            <Swipeable.Action {...props} color="cerise" onSwipe={onAction}>
              <ThemeIcon variant="transparent" color="white">
                <IconDelete />
              </ThemeIcon>
            </Swipeable.Action>
          )}
        >
          {children}
        </Swipeable>
      )}
    >
      <ExistingCreditCard onClick={onClick} credential={credential} {...props} />
    </ConditionalWrapper>
  );
};

ExistingCreditCard.Swipeable = SwipeableCard;

export const CredentialCardForm = ({
  credential,
  isActive = false,
  onClick,
  isGroupError = false,
}: {
  credential: ICredential;
  isActive?: boolean;
  onClick: () => void;
  isGroupError?: boolean;
}) => {
  return (
    <Stack gap={'xs'}>
      <Flex align={'center'} gap={'sm'} onClick={onClick}>
        <ExistingCreditCard credential={credential} isActive={isActive} isError={isGroupError} />
        <Transition mounted={isActive} transition="fade" timingFunction="ease">
          {(style) => (
            <Center style={style} maw={{ base: '100px', md: '107px' }} miw="95px">
              {isActive && <StripeCVV />}
            </Center>
          )}
        </Transition>
      </Flex>
    </Stack>
  );
};

export const ExistingCardGroup = ({ credentials }: { credentials: ICredential[] }) => {
  const form = useForm<ExistingCredentialsForm>({
    initialValues: {
      credentialId: undefined,
      card_type: '',
    },
    validate: {
      credentialId: isNotEmpty(),
    },
  });

  const handleCredentialChange = useCallback(
    (credential: ICredential) => {
      form.setFieldValue('credentialId', credential.uuid);
      form.setFieldValue('card_type', credential.card_type as PaymentMethodTypes);
      form.setFieldValue('isCvvValid', false);
    },
    [form]
  );

  const noCredentialError = form.errors.credentialId;

  useFormListValidator({ form, id: 'existing_credentials' });
  return (
    <Stack gap={'xs'}>
      <Text type="body" fw={600}>
        Select a Card on File
      </Text>
      {credentials?.map((credential: ICredential) => (
        <CredentialCardForm
          key={credential.uuid}
          credential={credential}
          onClick={() => handleCredentialChange(credential)}
          isActive={form.values.credentialId == credential.uuid}
          isGroupError={Boolean(noCredentialError)}
        />
      ))}
      {noCredentialError && (
        <Text fz={12} c={'cerise.7'}>
          Please select a payment method
        </Text>
      )}
    </Stack>
  );
};
